import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { ExpenseDocument } from '@peles/shared';
import * as expenseDocsApi from '../api/expense-documents';
import type { CompareQuoteItem } from '../api/expense-documents';

export function useExpenseDocuments(expenseItemId: string) {
  return useQuery<ExpenseDocument[]>({
    queryKey: ['expenseDocuments', expenseItemId],
    queryFn: () => expenseDocsApi.listExpenseDocuments(expenseItemId),
    enabled: !!expenseItemId,
  });
}

export function useAddExpenseDocument() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      expenseItemId,
      doc,
    }: {
      expenseItemId: string;
      doc: {
        s3Key: string;
        fileName: string;
        mimeType: string;
        fileSizeBytes: number;
        type?: string;
      };
    }) => expenseDocsApi.addExpenseDocument(expenseItemId, doc),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: ['expenseDocuments', variables.expenseItemId],
      });
    },
  });
}

export function useRemoveExpenseDocument() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ expenseItemId, docId }: { expenseItemId: string; docId: string }) =>
      expenseDocsApi.removeExpenseDocument(expenseItemId, docId),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: ['expenseDocuments', variables.expenseItemId],
      });
    },
  });
}

export function useCompareQuotes(eventId: string | undefined, categoryId: string | undefined) {
  return useQuery<CompareQuoteItem[]>({
    queryKey: ['compareQuotes', eventId, categoryId],
    queryFn: () => expenseDocsApi.compareQuotes(eventId!, categoryId!),
    enabled: !!eventId && !!categoryId,
  });
}
